/**
 * Niphean GTM 公共埋点工具
 * -----------------------------------------------------------------------------
 * 所有模块统一只使用这一个方法上报事件：
 *
 *   window.NipheanTracking.track({
 *     event: 'event_name',
 *     page_type: 'product_list',
 *     event_label: 'product_list_click_item',
 *     ...customParams
 *   });
 *
 * window.nipheanTrack(...) 是同一个方法的短别名，可按需使用
 *
 * 调用方只需要传业务埋点参数，以下公共字段会由本工具自动补齐：
 * - page_location：当前页面 URL
 * - page_referrer：上一页面 URL；没有上一页面时为空字符串
 * - device_type：来源终端，取值为 PC / Mobile / Tablet
 * - session_id：会话标识，存储在 sessionStorage / cookie 中
 * - user_id：用户标识；未登录时为空字符串
 *
 * ecommerce 参数说明：
 * - GA4 商品相关事件可直接传 ecommerce 对象
 * - 如果传入 ecommerce，本工具会先向 dataLayer 推送 { ecommerce: null }，
 *   再推送当前事件，避免上一次商品数据污染本次事件
 *
 * 普通事件示例：
 *
 *   window.NipheanTracking.track({
 *     event: 'customer_check_filter',
 *     page_type: 'product_list',
 *     event_label: 'product_list_check_filter'
 *   });
 *
 * 商品事件示例：
 *
 *   window.NipheanTracking.track({
 *     event: 'customer_click_item',
 *     page_type: 'product_list',
 *     event_label: 'product_list_click_item_image',
 *     ecommerce: {
 *       currency: 'USD',
 *       items: [{
 *         item_id: '123',
 *         item_name: 'Product name',
 *         item_category: 'Category',
 *         price: 199.99,
 *         index: 0
 *       }]
 *     }
 *   });
 *
 * 注意事项：
 * - event / page_type / event_label 需要严格按埋点文档传入
 * - 兼容旧写法：如果传入 event_category，会自动转为 page_type 并移除 event_category
 * - is_get_product 按文档传 yes 或 no
 */
(function (window, document) {
  'use strict';

  var SESSION_STORAGE_KEY = 'ga_sessionId';
  var COOKIE_SESSION_KEY = 'ga_cookieSessionId';
  var SESSION_MAX_AGE = 30 * 60;

  function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  function merge(target, source) {
    target = target || {};
    if (!source) return target;

    Object.keys(source).forEach(function (key) {
      target[key] = source[key];
    });

    return target;
  }

  function compact(value) {
    if (Array.isArray(value)) {
      return value.map(compact).filter(function (item) {
        return item !== undefined && item !== null;
      });
    }

    if (!isPlainObject(value)) return value;

    Object.keys(value).forEach(function (key) {
      if (value[key] === undefined || value[key] === null) {
        delete value[key];
        return;
      }

      value[key] = compact(value[key]);
    });

    return value;
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : '';
  }

  function setCookie(name, value, maxAge) {
    var cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
    if (window.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return '';
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // sessionStorage can be unavailable in privacy modes.
    }
  }

  function generateUUID() {
    if (window.crypto && window.crypto.getRandomValues) {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
        var random = window.crypto.getRandomValues(new Uint8Array(1))[0] % 16;
        var value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
      });
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      var random = Math.random() * 16 | 0;
      var value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function getSessionId() {
    var sessionId = storageGet(SESSION_STORAGE_KEY) || getCookie(COOKIE_SESSION_KEY);

    if (!sessionId) {
      sessionId = generateUUID();
    }

    storageSet(SESSION_STORAGE_KEY, sessionId);
    setCookie(COOKIE_SESSION_KEY, sessionId, SESSION_MAX_AGE);

    return sessionId;
  }

  function getUserId() {
    if (!window.gaGlobalVars || window.gaGlobalVars.userId === undefined || window.gaGlobalVars.userId === null) {
      return '';
    }

    return String(window.gaGlobalVars.userId);
  }

  function getCurrency() {
    if (window.gaGlobalVars && window.gaGlobalVars.currency) {
      return window.gaGlobalVars.currency;
    }

    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }

    return '';
  }

  function getDeviceType() {
    var ua = navigator.userAgent || '';
    var platform = navigator.platform || '';
    var maxTouchPoints = navigator.maxTouchPoints || 0;

    if (/iPad|Tablet/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1)) {
      return 'Tablet';
    }

    if (/Mobi|Android|iPhone|iPod/i.test(ua)) {
      return 'Mobile';
    }

    if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
      return 'Mobile';
    }

    return 'PC';
  }

  function commonPayload() {
    return {
      page_location: window.location.href,
      page_referrer: document.referrer || '',
      device_type: getDeviceType(),
      session_id: getSessionId(),
      user_id: getUserId()
    };
  }

  function normalizePayload(payload) {
    if (!payload) return payload;

    if (payload.page_type === undefined && payload.event_category !== undefined) {
      payload.page_type = payload.event_category;
    }

    delete payload.event_category;
    return payload;
  }

  function track(params) {
    if (!params || !params.event) return null;

    window.dataLayer = window.dataLayer || [];

    var payload = compact(normalizePayload(merge(commonPayload(), params)));

    if (payload.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }

    window.dataLayer.push(payload);
    return payload;
  }

  var api = window.NipheanTracking || {};
  api.track = track;
  api.getCommonPayload = commonPayload;
  api.getSessionId = getSessionId;
  api.getCurrency = getCurrency;

  window.NipheanTracking = api;
  window.nipheanTrack = track;
})(window, document);
